import {AfterViewInit, Component, ElementRef, Input, OnInit, OnChanges, SimpleChanges, ViewChild, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import * as atlas from 'azure-maps-control';
import {environment} from '../../../../environments/environment';
import Supercluster from 'supercluster'

export interface PlacePoint {
  name: string;
  latitude: number;
  longitude: number;
  description?: string;
}

@Component({
  selector: 'app-map',
  imports: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './map.component.html'
})
export class MapComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @Input() points: PlacePoint[] = []; // Additional points to display on the map
  @Input() zoom: number = 12; // Default zoom level
  @Input() setPointerOnLocation: boolean = true; // Whether to set a pointer on the location
  @Input() centerPoint?: PlacePoint; // Optional center point for the map
  @Input() enableClustering: boolean = false; // Enable clustering for large datasets

  @ViewChild('map') mapElement!: ElementRef<HTMLDivElement>;

  map!: atlas.Map;
  markers: atlas.HtmlMarker[] = [];
  superCluster?: Supercluster;
  private renderTimeout?: number;
  private lastRenderBounds?: atlas.data.BoundingBox;
  private lastRenderZoom?: number;
  private isRendering = false;

  constructor() {
  }

  ngOnDestroy(): void {
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
  }

  ngAfterViewInit(): void {
    const validZoom = this.getValidZoom(this.zoom, 12);

    // Calculate center point
    const centerCoords = this.calculateInitialCenter();

    this.map = new atlas.Map(this.mapElement.nativeElement, {
      center: centerCoords,
      zoom: validZoom,
      view: 'Auto',
      authOptions: {
        authType: atlas.AuthenticationType.subscriptionKey,
        subscriptionKey: environment.azMapsSubscriptionKey
      },
      style: 'road',

      // Ohne das bleibt die Karte auf jedem Screenshot weiss.
      //
      // WebGL verwirft den Zeichenpuffer standardmaessig, sobald das Bild auf dem
      // Schirm ist - wer den Canvas danach ausliest, bekommt eine leere Flaeche.
      // Genau das passiert dem bugBIC-Widget, das die Seite fuer den Bug-Report
      // aufnimmt: alles andere ist zu sehen, nur wo die Karte sass, ist es weiss.
      //
      // Die Option laesst den Puffer stehen und macht ihn damit lesbar. Sie kostet
      // etwas Zeichenleistung, weil der Browser ihn pro Bild aufheben muss - fuer
      // eine weitgehend statische Uebersichtskarte ist das vertretbar, und der
      // Nutzen ist unmittelbar: ein Bug-Report, auf dem man den Kartenausschnitt
      // sieht, um den es geht.
      preserveDrawingBuffer: true
    });

    this.map.events.add('ready', () => {
      this.initializeClustering();
      this.createMarkersForPoints();

      // Add optimized map events for clustering
      if (this.enableClustering) {
        // Use moveend for better performance instead of move
        this.map.events.add('moveend', () => this.debouncedRenderClusters());
        this.map.events.add('zoomend', () => this.debouncedRenderClusters());
      }
    });
  }

  ngOnInit(): void {

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.map) return;

    // Handle points changes
    if (changes['points'] && !changes['points'].firstChange) {
      this.initializeClustering();
      this.updateMarkers();
    }

    // Handle enableClustering changes
    if (changes['enableClustering'] && !changes['enableClustering'].firstChange) {
      this.initializeClustering();
      this.updateMarkers();
    }

    // Handle center point changes
    if (changes['centerPoint'] && !changes['centerPoint'].firstChange) {
      // this.updateMapCenter();
    }

    // Handle zoom changes
    if (changes['zoom'] && !changes['zoom'].firstChange) {
      this.updateMapZoom();
    }

    // Handle setPointerOnLocation changes
    if (changes['setPointerOnLocation'] && !changes['setPointerOnLocation'].firstChange) {
      this.updateMarkers();
    }
  }

  private calculateInitialCenter(): [number, number] {
    if (this.centerPoint && this.isValidPlacePoint(this.centerPoint)) {
      return [this.centerPoint.longitude, this.centerPoint.latitude];
    }

    if (this.points.length > 0) {
      const validPoints = this.points.filter(p => this.isValidPlacePoint(p));
      if (validPoints.length > 0) {
        const avgLng = validPoints.reduce((sum, p) => sum + p.longitude, 0) / validPoints.length;
        const avgLat = validPoints.reduce((sum, p) => sum + p.latitude, 0) / validPoints.length;
        return [avgLng, avgLat];
      }
    }

    // Default to Munich
    return [11.581, 48.135];
  }

  private debouncedRenderClusters(): void {
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }

    this.renderTimeout = window.setTimeout(() => {
      this.renderClusters();
    }, 100); // 100ms debounce
  }

  private initializeClustering(): void {
    if (this.enableClustering && this.points.length > 0) {
      this.superCluster = new Supercluster({
        radius: 30, // Increased radius for better clustering
        maxZoom: 16, // Increased max zoom
        minZoom: 0,
        minPoints: 2 // Minimum points to form a cluster
      });

      // Convert PlacePoints to GeoJSON features
      const features = this.points
        .filter(point => this.isValidPlacePoint(point))
        .map((point, index) => ({
          type: 'Feature' as const,
          properties: {
            name: point.name,
            description: point.description || '',
            cluster: false,
            pointIndex: index // Store original index for reference
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [parseFloat(point.longitude.toFixed(6)), parseFloat(point.latitude.toFixed(6))]
          }
        }));

      this.superCluster.load(features);
    } else {
      this.superCluster = undefined;
    }
  }

  private renderClusters(): void {
    if (!this.superCluster || !this.enableClustering || this.isRendering) {
      return;
    }

    this.isRendering = true;

    try {
      // Get the map's camera information
      const cam = this.map.getCamera();

      if (!cam.bounds || cam.zoom === undefined) {
        this.isRendering = false;
        return;
      }

      const currentZoom = Math.round(cam.zoom);
      const currentBounds = cam.bounds;

      // Check if we need to re-render (optimization)
      if (this.shouldSkipRender(currentBounds, currentZoom)) {
        this.isRendering = false;
        return;
      }

      // Get clusters for the current map's bounding box
      const points = this.superCluster.getClusters(
        [currentBounds[0], currentBounds[1], currentBounds[2], currentBounds[3]],
        currentZoom
      );

      // Clear existing markers efficiently
      this.clearMarkers();

      // Create markers for clusters and individual points
      const newMarkers: atlas.HtmlMarker[] = [];

      points.forEach((point: any) => {
        const marker = this.createClusterMarker(point);
        newMarkers.push(marker);
      });

      // Add all markers at once for better performance
      this.markers = newMarkers;
      this.map.markers.add(this.markers);

      // Update last render state
      this.lastRenderBounds = currentBounds;
      this.lastRenderZoom = currentZoom;

    } catch (error) {
      console.error('Error rendering clusters:', error);
    } finally {
      this.isRendering = false;
    }
  }

  private shouldSkipRender(bounds: atlas.data.BoundingBox, zoom: number): boolean {
    if (!this.lastRenderBounds || !this.lastRenderZoom) {
      return false;
    }

    // Skip if zoom didn't change and bounds didn't change significantly
    const zoomChanged = Math.abs(this.lastRenderZoom - zoom) >= 1;
    const boundsChanged = this.boundsChangedSignificantly(this.lastRenderBounds, bounds);

    return !zoomChanged && !boundsChanged;
  }

  private boundsChangedSignificantly(oldBounds: atlas.data.BoundingBox, newBounds: atlas.data.BoundingBox): boolean {
    const threshold = 0.001; // Adjust threshold as needed

    return Math.abs(oldBounds[0] - newBounds[0]) > threshold ||
           Math.abs(oldBounds[1] - newBounds[1]) > threshold ||
           Math.abs(oldBounds[2] - newBounds[2]) > threshold ||
           Math.abs(oldBounds[3] - newBounds[3]) > threshold;
  }

  private createClusterMarker(point: any): atlas.HtmlMarker {
    const position = new atlas.data.Position(
      point.geometry.coordinates[0],
      point.geometry.coordinates[1]
    );

    let htmlContent: string;

    if (point.properties.cluster) {
      // This is a cluster
      const abbreviatedCount = point.properties.point_count_abbreviated;

      // Dynamic cluster size based on point count
      const size = Math.min(60, Math.max(30, 30 + Math.log(point.properties.point_count) * 5));
      const fontSize = Math.min(16, Math.max(10, 10 + Math.log(point.properties.point_count) * 2));

      htmlContent = `
        <div class="select-none" style="
          background: #52AE32;
          color: white;
          border-radius: 50%;
          width: ${size}px;
          height: ${size}px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: ${fontSize}px;
          box-shadow: 0 3px 6px rgba(0,0,0,0.4);
          cursor: pointer;
          transition: transform 0.2s ease;
        ">
          ${abbreviatedCount}
        </div>
      `;
    } else {
      // This is an individual point
      htmlContent = `
        <div class="scale-75" style="width: 36px; height: 49px; cursor: pointer; transition: transform 0.2s ease;">
          <svg width="36" height="49" viewBox="0 0 36 49" fill="none">
            <path d="M0.009375 17.6813C0.009375 7.9125 8.07188 0 18.0094 0C27.9469 0 36.0094 7.9125 36.0094 17.6813C36.0094 28.8656 24.7406 42.2719 20.0344 47.3812C18.9281 48.5812 17.0813 48.5812 15.975 47.3812C11.2688 42.2719 0 28.8656 0 17.6813H0.009375ZM18.0094 24C19.6007 24 21.1268 23.3679 22.252 22.2426C23.3772 21.1174 24.0094 19.5913 24.0094 18C24.0094 16.4087 23.3772 14.8826 22.252 13.7574C21.1268 12.6321 19.6007 12 18.0094 12C16.4181 12 14.892 12.6321 13.7667 13.7574C12.6415 14.8826 12.0094 16.4087 12.0094 18C12.0094 19.5913 12.6415 21.1174 13.7667 22.2426C14.892 23.3679 16.4181 24 18.0094 24Z" fill="#005297"/>
          </svg>
        </div>
      `;
    }

    const marker = new atlas.HtmlMarker({
      position: position,
      htmlContent: htmlContent,
      visible: this.setPointerOnLocation
    });

    // Add popup and click handling
    if (point.properties.cluster) {

      this.map.events.add('click', marker, () => {
        // Get the expansion zoom level
        const expansionZoom = this.superCluster!.getClusterExpansionZoom(point.properties.cluster_id);
        const targetZoom = Math.min(expansionZoom + 1, 18);

        // Smooth zoom to expand cluster
        this.map.setCamera({
          center: position,
          zoom: targetZoom,
          type: 'fly',
          duration: 1000
        });
      });

      // Add hover effect for clusters
      this.map.events.add('mouseenter', marker, () => {
        marker.setOptions({
          htmlContent: htmlContent.replace('transform 0.2s ease', 'transform 0.2s ease; transform: scale(1.1)')
        });
      });

      this.map.events.add('mouseleave', marker, () => {
        marker.setOptions({
          htmlContent: htmlContent
        });
      });

    } else {
      // For individual points, show point info
      const placePoint: PlacePoint = {
        name: point.properties.name,
        latitude: point.geometry.coordinates[1],
        longitude: point.geometry.coordinates[0],
        description: point.properties.description
      };

      const popupContent = this.createPopupContent(placePoint);
      const popup = new atlas.Popup({
        content: popupContent,
        pixelOffset: [0, -25],
        closeButton: true
      });

      marker.setOptions({ popup: popup });

      this.map.events.add('click', marker, () => {
        marker.togglePopup();
      });
    }

    return marker;
  }

  private createMarkersForPoints(): void {
    this.clearMarkers();

    if (!this.setPointerOnLocation || this.points.length === 0) {
      return;
    }

    if (this.enableClustering && this.superCluster) {
      this.renderClusters();
    } else {
      // Use original marker creation for non-clustered view
      this.points.forEach(point => {
        if (this.isValidPlacePoint(point)) {
          const marker = this.createMarkerForPoint(point);
          this.markers.push(marker);
          this.map.markers.add(marker);
        }
      });
    }
  }

  private createMarkerForPoint(point: PlacePoint): atlas.HtmlMarker {
    const position = new atlas.data.Position(point.longitude, point.latitude);

    let marker = new atlas.HtmlMarker({
      color: '#009999',
      position: position,
      visible: this.setPointerOnLocation,
      htmlContent: '<div class="scale-75" style="width: 36px; height: 49px;"><svg width="36" height="49" viewBox="0 0 36 49" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.009375 17.6813C0.009375 7.9125 8.07188 0 18.0094 0C27.9469 0 36.0094 7.9125 36.0094 17.6813C36.0094 28.8656 24.7406 42.2719 20.0344 47.3812C18.9281 48.5812 17.0813 48.5812 15.975 47.3812C11.2688 42.2719 0 28.8656 0 17.6813H0.009375ZM18.0094 24C19.6007 24 21.1268 23.3679 22.252 22.2426C23.3772 21.1174 24.0094 19.5913 24.0094 18C24.0094 16.4087 23.3772 14.8826 22.252 13.7574C21.1268 12.6321 19.6007 12 18.0094 12C16.4181 12 14.892 12.6321 13.7667 13.7574C12.6415 14.8826 12.0094 16.4087 12.0094 18C12.0094 19.5913 12.6415 21.1174 13.7667 22.2426C14.892 23.3679 16.4181 24 18.0094 24Z" fill="#005297"/></svg></div>'
    });

    // Add popup with point information
    const popupContent = this.createPopupContent(point);
    const popup = new atlas.Popup({
      content: popupContent,
      pixelOffset: [0, -18]
    });

    marker.setOptions({
      popup: popup
    });

    this.map.events.add('click', marker, () => {
      marker.togglePopup();
    });

    return marker;
  }

  private createPopupContent(point: PlacePoint): string {
    return `
      <div class="p-3 max-w-xs">
        <h3 class="font-semibold text-lg mb-2">${point.name}</h3>
        ${point.description ? `<p class="text-sm text-gray-600">${point.description}</p>` : ''}
        <div class="text-xs text-gray-500 mt-2">
          <div>Lat: ${point.latitude.toFixed(6)}</div>
          <div>Lng: ${point.longitude.toFixed(6)}</div>
        </div>
      </div>
    `;
  }

  private clearMarkers(): void {
    this.markers.forEach(marker => {
      this.map.markers.remove(marker);
    });
    this.markers = [];
  }

  private updateMarkers(): void {
    if (this.enableClustering && this.superCluster) {
      this.renderClusters();
    } else {
      this.createMarkersForPoints();
    }
  }

  private isValidPlacePoint(point: PlacePoint): boolean {
    return point &&
      this.isValidCoordinate(point.latitude) &&
      this.isValidCoordinate(point.longitude) &&
      point.name.trim().length > 0;
  }

  private isValidCoordinate(value: number): boolean {
    return value != null && !isNaN(value) && isFinite(value);
  }

  private isValidZoom(value: number): boolean {
    return value != null && !isNaN(value) && isFinite(value) && value >= 0 && value <= 24;
  }

  private getValidZoom(value: number, defaultValue: number): number {
    return this.isValidZoom(value) ? value : defaultValue;
  }

  private updateMapCenter(): void {
    if (!this.map) return;

    let center: [number, number];

    if (this.centerPoint && this.isValidPlacePoint(this.centerPoint)) {
      center = [this.centerPoint.longitude, this.centerPoint.latitude];
    } else if (this.points.length > 0) {
      // Calculate center from all valid points
      const validPoints = this.points.filter(p => this.isValidPlacePoint(p));
      if (validPoints.length > 0) {
        const avgLng = validPoints.reduce((sum, p) => sum + p.longitude, 0) / validPoints.length;
        const avgLat = validPoints.reduce((sum, p) => sum + p.latitude, 0) / validPoints.length;
        center = [avgLng, avgLat];
      } else {
        return; // No valid points, don't update center
      }
    } else {
      return; // No points, don't update center
    }

    this.map.setCamera({
      center: center
    });
  }

  private updateMapZoom(): void {
    if (this.map && this.isValidZoom(this.zoom)) {
      this.map.setCamera({
        zoom: this.zoom
      });
    }
  }
}
